<template>
  <v-dialog
    data-testid="rejectApprovalDialog-container"
    width="650px"
    :persistent="isLoading"
    :value="show"
    @click:outside="handleClose"
  >
    <v-card :loading="isLoading">
      <div class="px-6 py-4">
        <v-card-title class="ma-0 pa-0 mb-4 flex-column align-start">
          <p class="ma-0 pa-0 h6 font-weight-600">
            {{ $t('routines.approval.reject_request') }}
          </p>
        </v-card-title>

        <v-card-text class="pa-0">
          <p
            class="body-2 blue-grey--text text--darken-2 mb-4"
            data-testid="rejectApprovalDialog-text-help"
          >
            {{ $t('routines.approval.reject_request_description') }}
          </p>

          <div
            class="d-flex flex-column my-4"
            data-testid="rejectApprovalDialog-section-rejectionReason"
          >
            <v-textarea
              v-model="rejectionReason"
              class="mt-2"
              auto-grow
              data-private
              data-testid="rejectApprovalDialog-textarea-rejectionReason"
              dense
              outlined
              :maxlength="maxlength"
              :counter="maxlength"
              :label="$t('routines.approval.rejection_reason') + '*'"
              :disabled="isLoading"
            />
          </div>
        </v-card-text>

        <v-card-actions class="justify-end ma-0 mt-8 pa-0">
          <Button
            class="me-4"
            color="grey"
            data-testid="rejectApprovalDialog-button-cancel"
            plain
            :disabled="isLoading"
            :text="$t('common.button.cancel')"
            @click="handleClose"
          />
          <Button
            color="danger"
            data-testid="rejectApprovalDialog-button-reject"
            :disabled="!canSubmit"
            :loading="isLoading"
            :text="$t('common.button.reject')"
            @click="handleSubmit"
          />
        </v-card-actions>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import _ from 'lodash'
import { mapGetters } from 'vuex'

import api from '@/api'
import mixin from '@/mixins/mixin.vue'
import Button from '@/components/UI/Button.vue'

export default {
  name: 'RejectApprovalDialog',

  components: {
    Button,
  },

  mixins: [mixin],

  props: {
    approval: {
      type: Object,
      required: true,
    },
    routineId: {
      type: String,
      required: true,
    },
    show: Boolean,
  },

  emits: ['close', 'rejected'],

  data() {
    return {
      isLoading: false,
      maxlength: 1200,
      rejectionReason: '',
    }
  },

  computed: {
    ...mapGetters(['account', 'organizationId']),

    trimmedReason() {
      return this.rejectionReason?.trim() || ''
    },

    canSubmit() {
      return !this.isLoading && !_.isEmpty(this.trimmedReason)
    },
  },

  watch: {
    show(value) {
      if (value) {
        this.resetForm()
      }
    },
  },

  methods: {
    resetForm() {
      this.rejectionReason = ''
      this.isLoading = false
    },

    handleClose() {
      if (this.isLoading) return
      this.$emit('close')
    },

    async handleSubmit() {
      if (!this.canSubmit) return
      if (!this.approval?.id) return

      this.isLoading = true

      try {
        await api.routine.updateRoutineApproval(
          this.organizationId,
          this.routineId,
          this.approval.id,
          {
            rejectionReason: this.trimmedReason,
            status: 'rejected',
          }
        )

        this.showSuccess(this.$t('routines.approval.reject_request_success'))
        this.$emit('rejected')
        this.$emit('close')
      } catch (error) {
        this.showError(this.$t('routines.approval.reject_request_failed'))
        this.handleRequestError(error)
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>
